<template>
  <div>
    <breadcrumbs :home="product.name + ': Return to tank'"/>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

            <v-row no-gutters>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh"/>
              </v-col>
            </v-row>

            <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :product="product" :availableCount="available" avaiableSuffix="Available" @change="changeQuantity"/>

            <v-row>
              <v-col cols="6" sm="3" lg="2"
                      v-if="company.settings.lotTrackingForProducts && product.alcohol.type!=='NonExcise'">
                <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
              </v-col>
              <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
                <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name" @change="transaction.tankId = ''"/>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" lg="4">
                <tank-select v-model="transaction.tankId" :tanks="siteTanks" label="Tank" outlined :hint="transactionId===undefined"
                            :highlight="tank => transactionId === undefined && tank.contents.alcoholId === product.alcohol.id"/>
              </v-col>
            </v-row>

            <v-row v-if="company.settings.lotTrackingForProducts && product.alcohol.type!=='NonExcise'">
              <v-col cols="12" sm="6" lg="3">
                <v-checkbox dense hide-details label="Return items to inventory" v-model="transaction.returnItems" @change="changeQuantity"/>
              </v-col>
            </v-row>
            <v-row v-if="company.settings.lotTrackingForProducts && product.alcohol.type!=='NonExcise'">
              <v-col cols="12" sm="6">
                <outlined-block :disabled="!transaction.returnItems">
                  <v-simple-table style="width:100%">
                    <thead>
                      <tr>
                        <th width="60%" class="text-left">Item</th>
                        <th width="40%" class="text-right">Quantity</th>
                      </tr>
                    </thead>
                    <tbody ref="tableBody">
                      <tr v-for="item in transaction.items" :key="item.id">
                        <td class="list-column">{{bomItem(item).name}}</td>
                        <td class="list-column">
                          <decimal-number-input :outlined="true" label="" v-model="item.quantity" prefix="" hide-details
                                :suffix="bomItem(item).trackUnits" :precision="bomItem(item).precision"/>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </outlined-block>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
        <v-divider/>
        <v-card-actions class="px-6">
          <template v-if="transactionId && company.permissions.includes('inventory.write')">
            <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                      v-model="edit" label="Edit" hide-details class="mt-0"/>
            <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
          </template>
          <v-spacer/>
          <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
          <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsReturnToTank',
  components: {TankSelect,CasesBottles},
  mixins: [confirmLeave],
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[],alcohol:{}},
      bom: null,
      items: [],
      tanks: [],
      valid:true,
      saving:false,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), tankId:'',
                    cases:0, bottles: 0, lot:'', comments: '',siteId:'',returnItems:false,items:null},
      dirty: false,
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.product.alcohol.type === 'NonExcise' ? undefined : {B262:true,B263:true,B265:true,B266:true});
    },
    siteTanks() {
      return this.tanks.filter(t => t.site.id === this.transaction.siteId);
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.siteId && site.inBondBottles)
                                .map(site => site.lot);
    },
    available() {
      let site;
      if (!this.company.settings.lotTrackingForProducts)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.siteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot);
      return (this.product.alcohol.type === 'NonExcise' ? site?.dutyPaidBottles : site?.inBondBottles) ?? 0;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    this.items = await this.$store.dispatch('items');
//    await this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,precision', 100, []);
    const results = await this.$apiCall(`query InventoryProductsReturnToTank($id:ID!,$edit:Boolean!) {
                                            productReturnToTank(id:$id) @include(if:$edit) {
                                              id,productId,date,cases,bottles,lot,comments,siteId,tankId,
                                              returnItems,items{id,quantity}
                                            }
                                          }`,
                                        {id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.transactionId) {
      this.$lazyListLoad(this.tanks, 'tanks2', 'id,name,site{id}', 100);
      this.transaction = results.data.productReturnToTank;
    }
    else {
      this.edit = true;
    }
    await this.refresh();
    if (!this.transactionId)
      this.transaction.siteId = this.sites[0].id;
    this.$nextTick(() => this.dirty = false);
    this.init = false;
  },

	methods: {
    bomItem(item) {
      const bomItem = this.bom ? this.bom.find(i => i.id === item.id) : null;
      return bomItem ? bomItem.item : {};
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsReturnToTank($id:ID!,$asof:DateTime!,$bom:Boolean!) {
                                              product(id:$id) {
                                                id,name,caseSize,mixedPack,containerType,alcohol{type,id}
                                                sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles}
                                                bom @include(if:$bom) {id,quantity,per}
                                              }
                                            }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date,
                                           bom:this.bom==null});
      if (!results.error) {
        this.product = results.data.product;
        if (!this.transactionId) {
          this.tanks = [];
          this.$lazyListLoad(this.tanks, 'tanks2', `id,name,site{id},contents{alcoholName,volume,alcoholType,alcoholId}`, 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.transaction.date}]);
        }
        if (!this.bom) {
          this.bom = results.data.product.bom.map(item => ({...item, item:this.items.find(i => i.id === item.id)}));
          if (!this.transaction.returnItems || !this.transaction.items)
            this.transaction.items = this.bom.map(item => ({id:item.id, quantity:0}));
          else  // Make sure all existing items are in current bom
            this.transaction.items = this.transaction.items.filter(item => this.bom.some(i => i.id === item.id));
        }
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    changeQuantity() {
      const bottles = this.transaction.cases * this.product.caseSize + this.transaction.bottles;
      const cases = this.$roundNumber(bottles / this.product.caseSize, 0);
      this.transaction.items.forEach(item => {
        const bomItem = this.bom.find(i => i.id === item.id);
        item.quantity = this.transaction.returnItems && bomItem ? bomItem.quantity * (bomItem.per === 'Bottle' ? bottles : (bomItem.per === 'Case' ? cases : 1)) : 0;
      });
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        const results = await this.$apiCall(`mutation InventoryProductsReturnToTank($input: ProductReturnToTankInput!) {
                                              productReturnToTankCreate(input:$input) {id}
                                            }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} return to tank transaction for ${this.product.name}`});
        }
      }
		}
	}
}
</script>